import { useState, useEffect } from 'react';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('genel');
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const heroSlides = [
    {
      title: "Geleceğin Gayrimenkul Yatırımları",
      subtitle: "ION Gayrimenkul Yatırım Ortaklığı olarak, halka arz sürecinde yatırımcılarımıza sürdürülebilir ve karlı gayrimenkul yatırım fırsatları sunuyoruz.",
      video: "https://public.readdy.ai/ai/video_res/8a84645f-fa6b-4b22-9a36-8e512b09c31f.mp4"
    },
    {
      title: "İstanbul'un Prestijli Lokasyonları",
      subtitle: "ION GYO is a global real estate developer that transforms urban landscapes into vibrant, people-centered communities.",
      video: "https://public.readdy.ai/ai/video_res/94d88a7a-ce0f-45d3-9be8-8409a61925f7.mp4"
    },
    {
      title: "Sürdürülebilir Büyüme Stratejisi",
      subtitle: "25 yıllık deneyimimiz ve güçlü portföyümüzle yatırımcılarımıza istikrarlı getiri sağlayan projeler geliştiriyoruz.",
      video: "https://public.readdy.ai/ai/video_res/b157847e-88cc-4927-bd55-49ec0f865f14.mp4"
    }
  ];

  const handleVideoChange = () => {
    // Video değiştirme işlevi - kullanıcı yeni video seçebilir
    const videoInput = document.createElement('input');
    videoInput.type = 'file';
    videoInput.accept = 'video/*';
    videoInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const videoUrl = URL.createObjectURL(file);
        // Aktif slaytta video değiştir
        const updatedSlides = [...heroSlides];
        updatedSlides[currentSlide].video = videoUrl;
        // Bu durumda state güncellemesi için useState kullanılabilir
        console.log('Video değiştirildi:', videoUrl);
      }
    };
    videoInput.click();
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="relative">
                <h1 className={`text-3xl font-bold transition-colors duration-300 ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`} style={{ fontFamily: "Pacifico, serif" }}>
                  ION GYO
                </h1>
                <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-teal-500 to-blue-500"></div>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              {['Hakkımızda', 'Projeler', 'Yatırımcı İlişkileri', 'Halka Arz', 'İletişim'].map((item, index) => (
                <a 
                  key={item}
                  href={`#${item.toLowerCase().replace(/\s+/g, '-').replace('ı', 'i')}`} 
                  className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                    isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-teal-500 to-blue-500 transition-all duration-300 hover:w-full"></span>
                </a>
              ))}
            </nav>
            <div className="md:hidden">
              <button className={`transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-400'
              }`}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Slider */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <video
              className="absolute inset-0 w-full h-full object-cover transform scale-105"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={slide.video} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>
          </div>
        ))}
        
        <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-4">
          <div className="animate-fade-in-up">
            <h2 className="text-6xl md:text-8xl font-bold mb-8 leading-tight bg-gradient-to-r from-white via-teal-200 to-blue-200 bg-clip-text text-transparent">
              {heroSlides[currentSlide].title.split(' ').map((word, index) => (
                <span 
                  key={index}
                  className="inline-block animate-bounce"
                  style={{ animationDelay: `${index * 0.2}s`, animationDuration: '2s' }}
                >
                  {word}&nbsp;
                </span>
              ))}
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed">
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="#halka-arz" className="group relative bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white px-10 py-5 rounded-full font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer overflow-hidden">
                <span className="relative z-10">Halka Arz Bilgileri</span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              <a href="#projeler" className="group relative border-2 border-white text-white hover:bg-white hover:text-gray-900 px-10 py-5 rounded-full font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer overflow-hidden">
                <span className="relative z-10">Projelerimizi İncele</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-gradient-to-br from-teal-400/20 to-blue-400/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/4 right-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full animate-bounce" style={{ animationDuration: '3s' }}></div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 to-blue-500/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '15+', label: 'Aktif Proje', icon: 'ri-building-line' },
              { number: '₺2.5M', label: 'Portföy Değeri', icon: 'ri-money-dollar-circle-line' },
              { number: '8', label: 'Şehir', icon: 'ri-map-pin-line' },
              { number: '25+', label: 'Yıl Deneyim', icon: 'ri-time-line' }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hakkımızda Section */}
      <section id="hakkimizda" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-teal-100/50 to-transparent rounded-full transform translate-x-48 -translate-y-48"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full text-teal-700 font-semibold mb-6">
                Hakkımızda
              </div>
              <h3 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                ION GYO <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Hakkında</span>
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                ION Gayrimenkul Yatırım Ortaklığı, Türkiye'nin önde gelen gayrimenkul yatırım şirketlerinden biri olarak, 
                halka arz sürecinde olan dinamik ve yenilikçi bir şirkettir.
              </p>
              <p className="text-lg text-gray-700 mb-10 leading-relaxed">
                Ticari, konut ve karma projeler geliştirerek yatırımcılarına sürdürülebilir getiri sağlamayı hedeflemekteyiz. 
                Modern portföy yönetimi anlayışı ile gayrimenkul sektöründe değer yaratmaya odaklanmaktayız.
              </p>
              
              <div className="grid grid-cols-2 gap-8">
                {[
                  { value: '15+', label: 'Aktif Proje' },
                  { value: '₺2.5M', label: 'Portföy Değeri' },
                  { value: '8', label: 'Şehir' },
                  { value: '25+', label: 'Yıl Deneyim' }
                ].map((item, index) => (
                  <div key={index} className="text-center p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2">
                    <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-2">
                      {item.value}
                    </div>
                    <div className="text-gray-600 font-medium">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-1 lg:order-2 relative">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-teal-500 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/39240058c63bb8e5e61eb264b84a05ff.jpg"
                  alt="ION GYO Yönetim Ekibi"
                  className="relative rounded-2xl shadow-2xl object-cover w-full h-96 transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projeler Section */}
      <section id="projeler" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-100/50 to-transparent rounded-full transform -translate-x-48 translate-y-48"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full text-teal-700 font-semibold mb-6">
              Projelerimiz
            </div>
            <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Prestijli</span> Projelerimiz
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              İstanbul'un en prestijli lokasyonlarında geliştirdiğimiz projelerle gayrimenkul sektöründe fark yaratıyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              {
                title: 'Nevbahar Uskudar',
                description: 'İstanbul\'un finans merkezi Maslak\'ta konumlanmış lüks konut projesi.',
                type: 'Konut',
                delivery: '2024 Teslim',
                image: 'https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/aef2fe5e629030439bfa590ff0fdec3e.jpeg'
              },
              {
                title: 'Istanbul Havalimanı',
                description: 'A+ sınıfı ofis binası ile Levent\'te prestijli iş merkezi.',
                type: 'Ticari',
                delivery: '2025 Teslim',
                image: 'https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/0352f73b65e9533d9ff9a3b865c9da2c.jpeg'
              },
              {
                title: 'Nevçarşı Shopping Mall',
                description: 'Karma kullanım projesi ile yaşam ve alışveriş merkezi.',
                type: 'Karma',
                delivery: '2026 Teslim',
                image: 'https://static.readdy.ai/image/4e090003b3774e06fbf8fa2737d5c315/aef2fe5e629030439bfa590ff0fdec3e.jpeg'
              }
            ].map((project, index) => (
              <div key={index} className="group relative bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4">
                <div className="relative overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Image Change Button */}
                  <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:scale-110 transition-all duration-300 shadow-lg">
                      <i className="ri-image-line text-lg"></i>
                    </button>
                  </div>
                  
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      project.type === 'Konut' ? 'bg-green-500 text-white' :
                      project.type === 'Ticari' ? 'bg-blue-500 text-white' :
                      'bg-purple-500 text-white'
                    }`}>
                      {project.type}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                    {project.title}
                  </h4>
                  <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500 font-medium">{project.delivery}</span>
                    <button className="w-10 h-10 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Halka Arz Section */}
      <section id="halka-arz" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-teal-50/50 to-blue-50/50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full text-teal-700 font-semibold mb-6">
              Halka Arz
            </div>
            <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Halka Arz <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Süreci</span>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              ION GYO olarak halka arz sürecimizde şeffaflık ve güven ilkelerimizle hareket ediyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="space-y-8">
                {[
                  {
                    icon: 'ri-check-line',
                    title: 'SPK Başvurusu Tamamlandı',
                    description: 'Sermaye Piyasası Kurulu\'na halka arz başvurumuz onaylandı.',
                    status: 'completed'
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'İzahname Hazırlığı',
                    description: 'Detaylı izahname ve finansal raporlarımız hazırlanıyor.',
                    status: 'progress'
                  },
                  {
                    icon: 'ri-calendar-line',
                    title: 'Halka Arz Tarihi',
                    description: '2024 yılı ikinci çeyreğinde halka arz gerçekleştirilecek.',
                    status: 'upcoming'
                  }
                ].map((step, index) => (
                  <div key={index} className="flex items-start space-x-6 group">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      step.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      step.status === 'progress' ? 'bg-gradient-to-r from-teal-500 to-blue-500' :
                      'bg-gradient-to-r from-gray-400 to-gray-500'
                    } group-hover:scale-110`}>
                      <i className={`${step.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                        {step.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-8 bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-lg border border-blue-100">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Yatırım Avantajları</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Güçlü portföy ve lokasyon avantajı',
                    'Deneyimli yönetim kadrosu',
                    'Sürdürülebilir büyüme stratejisi',
                    'Şeffaf kurumsal yönetim'
                  ].map((advantage, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-teal-500 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20financial%20trading%20floor%20with%20multiple%20screens%20showing%20stock%20market%20data%20and%20IPO%20information%2C%20professional%20investment%20banking%20environment%20with%20glass%20architecture%20and%20LED%20displays%2C%20contemporary%20corporate%20finance%20visualization&width=600&height=500&seq=ipo-process-001&orientation=landscape"
                  alt="Halka Arz Süreci"
                  className="relative rounded-2xl shadow-2xl object-cover w-full h-96 transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Yatırımcı İlişkileri Section */}
      <section id="yatirimci-iliskileri" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full text-teal-700 font-semibold mb-6">
              Yatırımcı İlişkileri
            </div>
            <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Yatırımcı</span> İlişkileri
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Yatırımcılarımızla şeffaf ve güvenilir iletişim kurarak değer yaratmaya odaklanıyoruz.
            </p>
          </div>

          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4">
              {['genel', 'finansal', 'kurumsal'].map((tab) => (
                <button 
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                    activeTab === tab 
                      ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-lg' 
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                  }`}
                >
                  {tab === 'genel' ? 'Genel Bilgiler' : 
                   tab === 'finansal' ? 'Finansal Raporlar' : 
                   'Kurumsal Yönetim'}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-10 shadow-xl">
            {activeTab === 'genel' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { value: '₺2.5 Milyar', label: 'Toplam Varlık', icon: 'ri-money-dollar-circle-line' },
                  { value: '%15.2', label: 'Yıllık Getiri', icon: 'ri-line-chart-line' },
                  { value: '850,000 m²', label: 'Toplam Alan', icon: 'ri-building-line' },
                  { value: '%92', label: 'Doluluk Oranı', icon: 'ri-pie-chart-line' }
                ].map((stat, index) => (
                  <div key={index} className="text-center group">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <i className={`${stat.icon} text-3xl text-white`}></i>
                    </div>
                    <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-3">
                      {stat.value}
                    </div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'finansal' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">2023 Yılı Finansal Özet</h4>
                    <div className="space-y-4">
                      {[
                        { label: 'Net Satışlar', value: '₺485 Milyon' },
                        { label: 'EBITDA', value: '₺125 Milyon' },
                        { label: 'Net Kar', value: '₺89 Milyon' }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-4 bg-white rounded-xl">
                          <span className="text-gray-600 font-medium">{item.label}</span>
                          <span className="font-bold text-lg bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Raporlar</h4>
                    <div className="space-y-4">
                      {[
                        '2023 Yıllık Rapor',
                        'Q3 2023 Finansal Rapor',
                        'Bağımsız Denetim Raporu'
                      ].map((report, index) => (
                        <a key={index} href="#" className="flex items-center justify-between p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group">
                          <span className="font-medium">{report}</span>
                          <i className="ri-download-line text-teal-600 group-hover:scale-110 transition-transform duration-300"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'kurumsal' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl">
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Yönetim Kurulu</h4>
                  <div className="space-y-6">
                    {[
                      { name: 'Mehmet Kalyoncu', position: 'Yönetim Kurulu Başkanı' },
                      { name: 'Ayşe Demir', position: 'Genel Müdür' },
                      { name: 'Can Yılmaz', position: 'Mali İşler Müdürü' }
                    ].map((member, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-xl">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center">
                          <i className="ri-user-line text-white text-2xl"></i>
                        </div>
                        <div>
                          <div className="font-bold text-lg text-gray-900">{member.name}</div>
                          <div className="text-gray-600">{member.position}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl">
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">Kurumsal Belgeler</h4>
                  <div className="space-y-4">
                    {[
                      'Ana Sözleşme',
                      'Faaliyet Raporu',
                      'Etik Kurallar'
                    ].map((document, index) => (
                      <a key={index} href="#" className="flex items-center justify-between p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group">
                        <span className="font-medium">{document}</span>
                        <i className="ri-download-line text-teal-600 group-hover:scale-110 transition-transform duration-300"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* İletişim Section */}
      <section id="iletisim" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/20 to-blue-900/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-500/20 to-blue-500/20 rounded-full text-teal-300 font-semibold mb-6 border border-teal-500/30">
              İletişim
            </div>
            <h3 className="text-5xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">Bizimle</span> İletişime Geçin
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Sorularınız için bizimle iletişime geçin. Uzman ekibimiz size yardımcı olmaktan memnuniyet duyar.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="space-y-10">
                {[
                  {
                    icon: 'ri-map-pin-line',
                    title: 'Adres',
                    content: 'Maslak Mahallesi, Büyükdere Caddesi\nNo: 123, Sarıyer/İstanbul'
                  },
                  {
                    icon: 'ri-phone-line',
                    title: 'Telefon',
                    content: '+90 212 555 0123'
                  },
                  {
                    icon: 'ri-mail-line',
                    title: 'E-posta',
                    content: 'info@iongyo.com.tr'
                  }
                ].map((contact, index) => (
                  <div key={index} className="flex items-start space-x-6 group">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <i className={`${contact.icon} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-3 text-white">{contact.title}</h4>
                      <p className="text-gray-300 leading-relaxed whitespace-pre-line">{contact.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-800/50 to-gray-700/50 p-10 rounded-3xl backdrop-blur-sm border border-gray-700/50">
              <h4 className="text-3xl font-bold mb-8 bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">Bize Ulaşın</h4>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Ad Soyad</label>
                    <input 
                      type="text" 
                      className="w-full px-6 py-4 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                      placeholder="Adınızı girin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">E-posta</label>
                    <input 
                      type="email" 
                      className="w-full px-6 py-4 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                      placeholder="E-posta adresinizi girin"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Konu</label>
                  <input 
                    type="text" 
                    className="w-full px-6 py-4 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300"
                    placeholder="Mesaj konusu"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Mesaj</label>
                  <textarea 
                    rows={4}
                    className="w-full px-6 py-4 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all duration-300 resize-none"
                    placeholder="Mesajınızı yazın..."
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white py-4 px-8 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap"
                >
                  Mesaj Gönder
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/10 to-blue-900/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="col-span-1 md:col-span-2">
              <h4 className="text-3xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent" style={{ fontFamily: "Pacifico, serif" }}>
                ION GYO
              </h4>
              <p className="text-gray-300 mb-8 max-w-md leading-relaxed">
                Türkiye'nin önde gelen gayrimenkul yatırım ortaklığı olarak, 
                yatırımcılarımıza sürdürülebilir değer yaratmaya devam ediyoruz.
              </p>
              <div className="flex space-x-4">
                {['linkedin', 'twitter', 'instagram'].map((social, index) => (
                  <a key={social} href="#" className="w-12 h-12 bg-gradient-to-br from-teal-600 to-blue-600 rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg group">
                    <i className={`ri-${social}-fill text-white group-hover:scale-110 transition-transform duration-300`}></i>
                  </a>
                ))}
              </div>
            </div>
            
            <div>
              <h5 className="text-xl font-bold mb-6 text-white">Hızlı Linkler</h5>
              <ul className="space-y-3">
                {['Hakkımızda', 'Projeler', 'Yatırımcı İlişkileri', 'Halka Arz'].map((link, index) => (
                  <li key={link}>
                    <a href={`#${link.toLowerCase().replace(/\s+/g, '-').replace('ı', 'i')}`} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer hover:translate-x-2 transform duration-300 inline-block">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h5 className="text-xl font-bold mb-6 text-white">İletişim</h5>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-teal-400"></i>
                  <span>Maslak, İstanbul</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line text-teal-400"></i>
                  <span>+90 212 555 0123</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line text-teal-400"></i>
                  <span>info@iongyo.com.tr</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700/50 mt-12 pt-8 text-center">
            <p className="text-gray-300">
              © 2024 ION Gayrimenkul Yatırım Ortaklığı. Tüm hakları saklıdır. | 
              <a href="https://readdy.ai/?origin=logo" className="text-teal-400 hover:text-teal-300 ml-1 transition-colors">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default HomePage;